/**
 * Triangular Dithering Processor
 * Priority 2: 10-15% quality improvement
 *
 * Breaks up quantization error correlation, reducing audible artifacts
 * during Float32→Int16 conversion. Essential for clean bit depth reduction.
 */

import { IAudioProcessor } from '../core/types'

/**
 * Dithering Processor using Triangular Probability Density Function (TPDF)
 *
 * Dithering is applied during the Float32→Int16 conversion stage to:
 * - Decorrelate quantization noise
 * - Convert correlated artifacts to white noise
 * - Preserve low-level detail
 * - Reduce harsh quantization artifacts
 *
 * This processor is a marker in the pipeline; actual dithering
 * occurs in AudioProcessor.convertToInt16()
 */
export class DitheringProcessor implements IAudioProcessor {
  getName(): string {
    return 'Dithering'
  }

  /**
   * Pass-through processor for Float32 buffer
   * Actual dithering applied during Int16 conversion
   * @param buffer Input audio samples
   * @returns Unmodified buffer
   */
  process(buffer: Float32Array): Float32Array {
    // Dithering is applied during conversion to Int16, not here
    return buffer
  }

  /**
   * Generate TPDF (Triangular Probability Density Function) dither
   * Used during Float32→Int16 conversion
   * @returns Dither value in range [-1/32768, +1/32768]
   */
  static generateDither(): number {
    // TPDF: sum of two uniform random variables
    // Creates triangular distribution with zero mean
    return (Math.random() - Math.random()) / 32768
  }

  /**
   * Apply dither to a single sample during Int16 conversion
   * @param sample Float32 sample (-1.0 to +1.0)
   * @returns Dithered sample ready for Int16 conversion
   */
  static applyDitherToSample(sample: number): number {
    const dithered = sample + DitheringProcessor.generateDither()
    // Clamp to valid range
    return Math.max(-1.0, Math.min(1.0, dithered))
  }

  reset(): void {
    // Stateless processor - no reset needed
  }
}
